import { CaretLeft, FileImage, Image } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "sonner";
import { HashLoader } from "react-spinners";
import useUtilityBillUpload from "../../../hooks/useUtilityBillUpload";
import { AnimatePresence, motion } from "framer-motion";
import { leaveSetupTask } from "../../../utils/setupTaskReturn";

const UtilityBill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;

  const { refetch } = useAuth();

  const ref = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
    }
  };

  const {
    uploadUtilityBill,
    uploadingUtilityBill,
    isUtilityBillUploadError,
    idUploadError,
  } = useUtilityBillUpload({
    onSuccess() {
      toast.success("Utility bill uploaded successfully");
      refetch();
      if (fromSetup) leaveSetupTask(navigate, location.state);
      else navigate("/home/settings/compliance");
    },
    onError() {
      toast.error("An error occurred uploading utility bill");
      refetch();
    },
  });

  const handleSubmit = () => {
    if (!file) return;
    uploadUtilityBill({ file });
  };
  return (
    <>
      {uploadingUtilityBill && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <div className="flex items-center justify-center gap-10 px-5 py-5 pb-20 flex-col">
        <div className="flex-col gap-4 flex items-start justify-center w-full max-w-[32rem]">
          <div className="bg-white p-8 border border-gray-200 rounded-xl w-full flex flex-col gap-5">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start"
            >
              <CaretLeft weight="bold" size={20} />
            </button>
            <div className="flex flex-col gap-2">
              <h1 className="font-medium text-3xl">Upload Utility Bill</h1>
              <div className="text-gray-800">
                Please upload a clear photo of your valid utility bill
                (Electricity, Water, or Gas)
              </div>
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={handleFileChange}
              ref={ref}
              className="hidden"
            />
            <AnimatePresence>
              {file ? (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-3 border border-gray-300 p-4 rounded-lg"
                >
                  <div>
                    <FileImage size={28} />
                  </div>
                  <div className="flex flex-col flex-1">
                    <div className="text-sm font-medium text-gray-800">
                      {file.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      Size: {(file.size / 1024).toFixed(2)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                    }}
                    className="text-sm hover:text-red-400 cursor-pointer transition-all"
                  >
                    Remove
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  type="button"
                  onClick={() => {
                    ref.current?.click();
                  }}
                  className="flex p-4 border border-gray-300 rounded-lg flex-col items-center justify-center gap-2 hover:bg-gray-50 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2 bg-gray-100 p-4 rounded-md">
                    <Image size={32} />
                  </div>
                  <div className="font-medium text-lg">Choose File</div>
                  <div className="text-sm">JPEG, JPG, PNG, PDF max 5MB</div>
                </motion.button>
              )}
            </AnimatePresence>

            <ul className="flex flex-col gap-2 list-disc list-inside text-gray-800">
              <li>Upload utility bill not older than 3 months</li>
              <li>Ensure the bill is clear and readable</li>
              <li>Avoid glare or blurriness</li>
            </ul>
            {isUtilityBillUploadError && (
              <div className="text-red-500">{idUploadError?.message}</div>
            )}
            <div className="w-full flex gap-5">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
              >
                <CaretLeft weight="bold" size={20} />
                <span>Back</span>
              </button>

              <button
                disabled={!file}
                type="submit"
                onClick={handleSubmit}
                className="p-2 py-3 text-center flex-1 bg-deepBlue text-white px-10 border border-deepBlue rounded-xl font-medium w-full"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UtilityBill;
